import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../../assets/Icons";
import SmallPopupWithInput from "../../../../../components/_utils/CommonSmallPopupWithInput";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import CommonToast from "../../../../../components/_utils/CommonToast";
import { formatAccountNumber } from "../../../../../components/_utils/FormatAccountNumber";
import UniversalRefreshToast from "../../../../../components/_utils/UniversalRefreshToast";
import {
  approveBeneficiaryAccount,
  beneficiaryAccountDetailsById,
  rejectBeneficiaryAccount,
} from "../../../../../redux/features/accounts/beneficiaryAccount/index.js";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import {
  bankLookUp,
  panLookUp,
} from "../../../../../redux/features/sharedResources/sharedResources";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import BeneAccountDeclinePopup from "../../../../../components/Accounts/BeneficiaryAccount/BeneAccountDeclinePopup/index.js";

function BeneficiaryAccountEditBankSuccess() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const updatedFields = location?.state?.updatedFields || [];
  const [showLoader, setShowLoader] = useState(false);
  const [rejectPopup, setRejectPopup] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [currentAccountDetails, setCurrentAccountDetails] = useState({
    id: "",
    name: "",
    bank_name: "",
    bank_acc_no: "",
    ifsc_code: "",
    categoryIds: [],
    pan: "",
    labels: "",
  });

  const services = useSelector((state) => state.refreshToken.services);
  const { beneficiaryAccountDetailsData } = useSelector(
    (state) => state.beneficiaryAccount,
  );
  const { panLookupData, bankLookupData } = useSelector(
    (state) => state.sharedResources,
  );

  const beneAccountSuccessData =
    beneficiaryAccountDetailsData?.beneficiary_accounts[0];

  const canApprove =
    beneAccountSuccessData?.approval_status === "pending" &&
    services?.includes("BAAccount");

  const canCreateTxn = services?.includes("CBeneficiaryTxn");

  const fieldsToShow =
    updatedFields.length > GlobalConfiguration.lengthCheckZero
      ? updatedFields
      : [
          "bank_acc_no",
          "ifsc_code",
          "bank_account_holder_name",
          "category",
          "account_name",
          "pan_no",
          "contact_no",
          "email_id",
        ];

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === 0) return "default";

    if (length >= GlobalConfiguration.rejectReasonMinLength) {
      return "verified";
    }
    return focused ? "typing" : "error";
  };

  const handleInputChange = (event) => {
    let value = event?.target?.value;
    value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    value = value.replace(/^\s+/, "");
    value = value.replace(/\s{2,}/g, " ");
    value = value.slice(0, 150);
    setDeclineReason(value);
  };

  const bankFieldConfig = {
    bank_acc_no: {
      leftSubTitle: "Account number",
      getTitle: (acc) => formatAccountNumber(acc?.bank_acc_no),
      ifsc: true,
    },
    ifsc_code: {
      leftSubTitle: "IFSC",
      getTitle: (acc) => acc?.ifsc_code,
    },
    category: {
      leftSubTitle: "Assigned categories",
      getTitle: (acc) => {
        const names = (acc?.category_ids || [])
          .map((c) => c?.category_name)
          .filter(Boolean);
        return names.length ? names.join(", ") : "Not assigned";
      },
    },
    account_name: {
      leftSubTitle: "Account name",
      getTitle: (acc) => acc?.name,
    },
    pan_no: {
      leftSubTitle: "PAN number",
      getTitle: (acc) => acc?.pan_no,
    },
    contact_no: {
      leftSubTitle: "Contact number",
      icon: ICONS.CONTACT_NO,
      getTitle: (acc) =>
        acc?.contact_no ? `+91 ${acc.contact_no}` : "Not available",
    },
    email_id: {
      leftSubTitle: "Email ID",
      icon: ICONS.GMAIL_RED_ERROR,
      getTitle: (acc) => acc?.email_id || "Not available",
    },
  };

  const renderAccountDetailCards = () => {
    return fieldsToShow
      .filter((key) => bankFieldConfig[key])
      .map((key) => {
        const config = bankFieldConfig[key];
        return (
          <InfoCard
            key={key}
            bgColor="var(--positive-card)"
            rows={[
              {
                leftTitle: config.getTitle(beneAccountSuccessData),
                leftSubTitle: config.leftSubTitle,
                ...(config.icon && { icon: config.icon }),
                ...(config.ifsc &&
                  beneAccountSuccessData && {
                    ifsc: beneAccountSuccessData.ifsc_code,
                  }),
              },
            ]}
          />
        );
      });
  };

  const handlers = {
    createAccount: () =>
      navigate("/beneficiary_account_dashboard/create_beneficiary_account", {
        replace: true,
      }),
    createTxn: () => {
      if (!services?.includes("CBeneficiaryTxn")) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "CBeneficiaryTxn" },
        });
        return;
      }

      navigate(
        "/beneficiary_transaction_dashboard/create_beneficiary_transaction",
        {
          state: currentAccountDetails,
          replace: true,
        },
      );
    },
    viewDetails: () =>
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
        {
          replace: true,
        },
      ),
    dashboard: () =>
      navigate("/beneficiary_account_dashboard", {
        replace: true,
      }),
  };

  const accountApproval = () => {
    (async () => {
      setShowLoader(true);
      toast.dismiss();
      UniversalRefreshToast("approvalaccountsRefresh");
      const response = await dispatch(approveBeneficiaryAccount({ id }));
      if (approveBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();

        CommonToast({
          text: "Beneficiary account approved.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        setShowLoader(false);
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });
      }
    })();
  };

  const accountRejected = () => {
    (async () => {
      setShowLoader(true);
      toast.dismiss();
      UniversalRefreshToast("accountsRefresh");

      const response = await dispatch(
        rejectBeneficiaryAccount({
          id,
          rejection_reason: declineReason,
        }),
      );

      if (rejectBeneficiaryAccount.fulfilled.match(response)) {
        toast.dismiss();

        CommonToast({
          text: "Beneficiary account rejected.",
          variant: "var(--success)",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        });

        navigate(
          `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${id}`,
          { replace: true },
        );
      } else {
        setShowLoader(false);
        toast.dismiss();
        CommonToast({
          text: response?.payload?.errors?.beneficiary_account?.[0],
          variant: "var(--error)",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        });

        setRejectPopup(false);
      }
    })();
  };

  const primaryBtn = {
    enabledBgColor: "var(--text-primary)",
    enabledTextColor: "var(--positive-bg)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    imgColor: "var(--positive-bg)",
    hoverOption: "dark",
  };

  const outlineBtn = {
    enabledBgColor: "var(--bg-positive-bg)",
    enabledTextColor: "var(--text-primary)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    outLineColor: "var(--text-primary)",
    hoverOption: "dark",
  };

  const buttonRows = [
    ...(canApprove
      ? [
          [
            {
              btnText: globalText?.REJECT_ACCOUNT,
              enabledBgColor: "var(--negative-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.REJECT_ICON_RED_WITH_WHITE_CIRCLE,
              handleFunction: () => {
                if (!showLoader) {
                  setRejectPopup(true);
                }
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
              isDisabled: showLoader,
            },
            {
              btnText: globalText?.APPROVE_ACCOUNT,
              enabledBgColor: "var(--positive-act)",
              enabledTextColor: "var(--text-primary)",
              icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
              handleFunction: () => {
                if (!showLoader) {
                  accountApproval();
                }
              },
              iconToBeAlign: "left",
              fullWidthRequired: true,
              isDisabled: showLoader,
            },
          ],
        ]
      : []),

    [
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_ACCOUNT,
        icon: ICONS?.CREATE_NEW_ACCOUNT,
        handleFunction: handlers.createAccount,
        isDisabled: showLoader,
      },
      {
        ...primaryBtn,
        btnText: globalText?.CREATE_TRANSACTION,
        icon: ICONS?.ADD_BUTTON_WHITE,
        handleFunction: handlers.createTxn,
        isDisabled: !canCreateTxn || showLoader,
      },
      {
        ...primaryBtn,
        btnText: globalText?.VIEW_DETAILS,
        icon: ICONS?.VIEW_DETAILS,
        handleFunction: handlers.viewDetails,
        isDisabled: showLoader,
      },
      {
        ...outlineBtn,
        btnText: globalText?.GO_TO_DASHBOARD,
        icon: ICONS?.GO_TO_DASHBOARD,
        handleFunction: handlers.dashboard,
        imgColor: "var(--text-primary)",
        isDisabled: showLoader,
      },
    ],
  ];

  useEffect(() => {
    dispatch(beneficiaryAccountDetailsById({ id }));
    dispatch(setHideBreadCrumb({ hide: true }));

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (!beneAccountSuccessData) return;

    dispatch(panLookUp({ pan_number: beneAccountSuccessData.pan_no }));
    dispatch(
      bankLookUp({
        ifsc: beneAccountSuccessData.ifsc_code,
        bank_account_no: beneAccountSuccessData.bank_acc_no,
      }),
    );
  }, [beneAccountSuccessData, dispatch]);

  useEffect(() => {
    if (beneAccountSuccessData) {
      const {
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category_ids,
        pan_no,
        labels,
      } = beneAccountSuccessData;
      const categoryIds = (category_ids || []).map(
        (c) => c.category_id ?? c.id,
      );
      setCurrentAccountDetails({
        id,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        categoryIds,
        pan: pan_no,
        labels: labels,
      });
    }
  }, [beneAccountSuccessData]);

  return (
    <>
      {rejectPopup && (
        <BeneAccountDeclinePopup
          headerTitle={globalText?.REJECT_REASON_LABEL}
          bulletPoints={[
            globalText.REJECT_POPUP_BULLET_POINT_ONE,
            globalText.REJECT_POPUP_BULLET_POINT_TWO,
            globalText.REJECT_POPUP_BULLET_POINT_THREE,
          ]}
          declineReason={declineReason}
          setDeclineReason={setDeclineReason}
          showLoader={showLoader}
          setShowLoader={setShowLoader}
          handleClosePopup={() => {
            setRejectPopup(false);
            setDeclineReason("");
          }}
          handleFunctionClick={() => {
            accountRejected();
            setDeclineReason("");
          }}
        />
      )}
      <div>
        <StatusContainer
          status="success"
          buttonRows={buttonRows}
          title={
            "Old account archived and new account added to the list of active beneficiary accounts successfully."
          }
        >
          <TabsWeapperSection title="Account details">
            <>
              {renderAccountDetailCards()}
              {beneAccountSuccessData?.virtual_account === "Y" && (
                <InfoCard
                  bgColor="var(--positive-card)"
                  rows={[
                    {
                      leftTitle:
                        beneAccountSuccessData?.virtual_account_type ===
                        "manual"
                          ? "Manually created"
                          : "Auto detected",
                      leftSubTitle: "Virtual account",
                    },
                  ]}
                />
              )}
              <InfoCard
                bgColor="var(--positive-card)"
                rows={[
                  {
                    leftTitle: beneAccountSuccessData?.id,
                    leftSubTitle: "Account ID",
                    copyText: beneAccountSuccessData?.id,
                  },
                ]}
              />
            </>
          </TabsWeapperSection>
          {panLookupData?.beneficiary_accounts?.length >
            GlobalConfiguration.lengthCheckOne && (
            <TabsWeapperSection title="Accounts associated to PAN">
              {panLookupData?.beneficiary_accounts
                ?.filter(
                  (beneAccountSuccessData) =>
                    beneAccountSuccessData?.approval_status === "pending" ||
                    beneAccountSuccessData?.approval_status === "approved",
                )
                .map((beneAccountSuccessData, id) => (
                  <InfoCard
                    key={id}
                    bgColor="var(--positive-card)"
                    showRowDivider={true}
                    rowBorderColor="var(--positive-bg)"
                    borderColor="var(--positive-bg)"
                    rightHeaderTextColor="var(--text-secondary)"
                    leftHeaderWidth="w-width-lg"
                    rows={[
                      {
                        leftTitle:
                          beneAccountSuccessData?.bank_account_holder_full_name,
                        leftSubTitle: `${
                          beneAccountSuccessData?.approval_status
                            ?.charAt(0)
                            .toUpperCase() +
                          beneAccountSuccessData?.approval_status?.slice(1)
                        } account`,
                      },

                      {
                        leftTitle: formatAccountNumber(
                          beneAccountSuccessData?.bank_acc_no,
                        ),
                        leftSubTitle: "Account number",
                        ifsc: beneAccountSuccessData?.ifsc_code,
                      },
                    ]}
                  />
                ))}
            </TabsWeapperSection>
          )}

          {bankLookupData?.beneficiary_accounts?.length >
            GlobalConfiguration.lengthCheckOne && (
            <TabsWeapperSection title="Accounts associated to bank">
              {bankLookupData?.beneficiary_accounts
                .filter(
                  (beneAccountSuccessData) =>
                    beneAccountSuccessData?.approval_status === "pending" ||
                    beneAccountSuccessData?.approval_status === "approved",
                )
                .map((beneAccountSuccessData, id) => (
                  <InfoCard
                    key={id}
                    bgColor="var(--positive-card)"
                    borderColor="var(--positive-bg)"
                    showRowDivider={true}
                    rowBorderColor="var(--positive-bg)"
                    rightHeaderTextColor="var(--text-secondary)"
                    leftHeaderWidth="w-width-lg"
                    rows={[
                      {
                        leftTitle: beneAccountSuccessData?.name,
                        leftSubTitle: `${
                          beneAccountSuccessData?.approval_status
                            ?.charAt(0)
                            .toUpperCase() +
                          beneAccountSuccessData?.approval_status?.slice(1)
                        } account`,
                      },
                      {
                        leftTitle: beneAccountSuccessData?.pan_no,
                        leftSubTitle: "PAN",
                      },
                    ]}
                  />
                ))}
            </TabsWeapperSection>
          )}
        </StatusContainer>
      </div>
    </>
  );
}

export default BeneficiaryAccountEditBankSuccess;
